import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { notificationService } from '../services/notification';
import { useAuth } from './AuthContext'; // Import AuthContext

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  isRead: boolean;
  createdAt: string;
  projectInvite?: {
    projectId: string;
    projectName: string;
  };
  boardInvite?: {
    boardId: string;
    boardName: string;
    projectId: string;
  };
}

interface NotificationContextData {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  loadUnreadCount: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  handleNotificationAction: (notificationId: string, action: 'accept' | 'reject') => Promise<void>;
  // Nova função para forçar atualização
  forceRefresh: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextData>({
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
  loadUnreadCount: async () => {},
  refreshNotifications: async () => {},
  markAsRead: async () => {},
  markAllAsRead: async () => {},
  handleNotificationAction: async () => {},
  forceRefresh: async () => {}
} as NotificationContextData);

// Exporta o contexto para uso direto em componentes
export { NotificationContext };

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth(); // Pega o usuário autenticado
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [isLoadingCount, setIsLoadingCount] = useState(false);
  
  // Usar refs para prevenir chamadas concorrentes sem dependências
  const isRefreshingRef = useRef(false);

  const loadUnreadCount = useCallback(async () => {
    // SÓ carrega se o usuário estiver autenticado
    if (!user || !user.id) {
      return;
    }

    // Previne chamadas concorrentes
    if (isLoadingCount) {
      return;
    }

    try {
      setIsLoadingCount(true);
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
      setInitialLoadDone(true);
    } catch (err) {
      console.error('[❌ NotificationContext] Erro ao carregar contador:', err);
      // NÃO faz fallback que pode causar loop - apenas define 0
      setUnreadCount(0);
    } finally {
      setIsLoadingCount(false);
    }
  }, []); // SEM dependências - usar closure para acessar o user

  // Load inicial APENAS quando usuário autenticar
  useEffect(() => {
    if (user?.id) {
      loadUnreadCount();
    } else {
      // Reset states quando não tem usuário
      setNotifications([]);
      setUnreadCount(0);
      setInitialLoadDone(false);
    }
  }, [user?.id]); // Apenas user?.id como dependência

  const refreshNotifications = useCallback(async () => {
    // SÓ carrega se o usuário estiver autenticado
    if (!user || !user.id) {
      return;
    }

    // usa ref para evitar dependência no useCallback
    if (isRefreshingRef.current) {
      return;
    }

    isRefreshingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const data = await notificationService.getNotifications();
      const notificationsList = Array.isArray(data) ? data : [];
      setNotifications(notificationsList);
      
      // Atualiza o count baseado nas notificações carregadas
      const count = notificationsList.filter((n: Notification) => !n.isRead).length;
      setUnreadCount(count);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar notificações');
      console.error('[❌ NotificationContext] Erro ao carregar notificações:', err);
      setNotifications([]);
    } finally {
      setLoading(false);
      isRefreshingRef.current = false;
    }
  }, []); // SEM dependências - usar closure para acessar o user

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);
      
      // Atualiza o estado local imediatamente
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
      throw err;
    }
  }, []); // SEM dependências

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      
      // Atualiza o estado local imediatamente
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      throw err;
    }
  }, []); // SEM dependências

  const handleNotificationAction = useCallback(async (notificationId: string, action: 'accept' | 'reject') => {
    try {
      
      // Encontra a notificação antes de processar
      const notification = notifications.find(n => n.id === notificationId);
      const wasUnread = notification && !notification.isRead;
      
      await notificationService.handleNotificationAction(notificationId, action);
      
      // Remove a notificação da lista
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      // Atualiza contador se a notificação era não lida
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
    } catch (err) {
      console.error('Error handling notification action:', err);
      throw err;
    }
  }, [notifications]); // Dependência APENAS do notifications que já está no estado

  // Função simplificada para atualização manual
  const forceRefresh = useCallback(async () => {
    
    // Chama refreshNotifications diretamente sem verificar loading
    // (refreshNotifications já faz essa verificação internamente)
    await refreshNotifications();

  }, []); // SEM dependências - evita loop infinito

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        error,
        loadUnreadCount,
        refreshNotifications,
        markAsRead,
        markAllAsRead,
        handleNotificationAction,
        forceRefresh
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
